/**
 * @sharpee/text-blocks
 *
 * Pure interfaces for structured text output.
 * No runtime dependencies - types only.
 *
 * @see ADR-096: Text Service Architecture
 * @see ADR-091: Text Decorations
 */

/**
 * Content within a text block - either plain string or decorated content.
 *
 * @example
 * // Plain text
 * const plain: TextContent = "You take the sword.";
 *
 * // Decorated content (post ADR-174)
 * const decorated: TextContent = {
 *   className: 'sharpee-item',
 *   content: ['brass lantern']
 * };
 */
export type TextContent = string | IDecoration | IChosen;

/**
 * A span whose text was *selected from alternatives* by consulting world state
 * (ADR-364 D4) — a conditional `room name` arm, a presence-gated snippet, a
 * state-derived detail clause, a slot's occupant contribution, a `Choice` pick.
 *
 * This is provenance, not presentation. It is deliberately NOT an `IDecoration`:
 * a decoration is an author's CSS hook and a renderer styles it, while this says
 * only "the world chose this text this turn" and every renderer passes through it
 * transparently, exactly as though the span were bare. Conflating the two would
 * give one type two reasons to change.
 *
 * Its consumer is auto-assertion (ADR-364 D4): a synthesized claim pins the
 * spans that came out the same way regardless and skips these, so a test does
 * not break when an NPC walks off or a gate flips. Nothing a player sees
 * depends on it.
 */
export interface IChosen {
  /** Discriminator — always `true`; its presence is the whole signal. */
  readonly chosen: true;
  /** The selected content. May nest decorations, which keep their meaning. */
  readonly content: ReadonlyArray<TextContent>;
}

/**
 * Decorated content with a final, fully-resolved CSS class name.
 *
 * Per ADR-174, the bracket parser resolves bracketed names against a
 * closed platform vocabulary at parse time:
 *  - Platform names (e.g. `em`, `item`, `color-red`) become
 *    `sharpee-{name}` and are styled by the platform CSS bundle.
 *  - Author names pass through verbatim and the story owns their CSS.
 *
 * Renderers translate `IDecoration` to their target output (HTML span
 * for browser, ANSI for terminal, ignored for audio).
 *
 * @example
 * // Platform emphasis (resolved from `[em:carefully]`)
 * { className: 'sharpee-em', content: ['carefully'] }
 *
 * // Platform item name
 * { className: 'sharpee-item', content: ['brass lantern'] }
 *
 * // Author class
 * { className: 'thief-taunt', content: ["You'll regret this."] }
 *
 * // Nested decorations
 * { className: 'sharpee-item', content: [
 *   { className: 'sharpee-em', content: ['glowing'] },
 *   ' lantern'
 * ]}
 */
export interface IDecoration {
  /**
   * Final, fully-resolved CSS class name. Already prefixed with
   * `sharpee-` for platform-vocabulary names; bare for author names.
   * @see ADR-174 §Wire shape
   */
  readonly className: string;

  /**
   * Content within the decoration.
   * Can contain nested decorations or plain strings.
   */
  readonly content: ReadonlyArray<TextContent>;

  /**
   * Optional value carried by a parameterized decoration (ADR-183) —
   * e.g. the width `%` for `sharpee-center`, the level for
   * `sharpee-indent`. Renderers emit it as a `data-value` attribute,
   * never as an inline style. Absent for non-parameterized decorations.
   * @see ADR-183 §3 — Wire + render
   */
  readonly value?: string;
}

/**
 * A block of text output with semantic key (channel).
 *
 * Keys act as channels (FyreVM pattern). Clients route blocks by key:
 * - `room.name` - Room title
 * - `room.description` - Room description
 * - `room.contents` - Items in room
 * - `action.result` - Action outcome
 * - `action.blocked` - Why action failed
 * - `status.room` - Current location (status bar)
 * - `status.score` - Current score (status bar)
 * - `status.turns` - Turn count (status bar)
 * - `error` - System errors
 * - `prompt` - Command prompt
 * - Story-defined keys: 'dungeo.thief.taunt', etc.
 *
 * @example
 * // Room name block
 * {
 *   key: 'room.name',
 *   content: [{ className: 'sharpee-room', content: ['West of House'] }]
 * }
 *
 * // Action result with decorated item
 * {
 *   key: 'action.result',
 *   content: [
 *     'You take ',
 *     { className: 'sharpee-item', content: ['the brass lantern'] },
 *     '.'
 *   ]
 * }
 *
 * // Status bar block
 * {
 *   key: 'status.score',
 *   content: ['42']
 * }
 */
/**
 * Where a block's text came from (ADR-333 D1): the message id the prose
 * pipeline rendered to produce it — a Chord phrase key, a platform
 * message id, or a story override's id. The id is the whole stamp; a
 * consumer that holds the story's IR or a language pack resolves it to a
 * source span or a template (ADR-333 D2). Absent on blocks that were not
 * rendered from a message (inline fallback text, banner pieces) — absence
 * means "not a message", never "unknown".
 */
export interface IBlockSource {
  /** The message id the block was rendered from. Never empty. */
  readonly messageId: string;
  /**
   * The primitive facts the source event carried beside its message —
   * its top-level string, number, and boolean fields (`targetId`,
   * `targetName`, `topic`, …), never the rendering `params`, the inline
   * `message`/`text`, or the id itself. A consumer that wants to know WHO
   * a reply was about and WHAT it concerned reads these (ADR-333 D1 as
   * amended 2026-09-06: the id names the message; the facts name the
   * occasion). Absent when the event carried none.
   */
  readonly facts?: Readonly<Record<string, string | number | boolean>>;
}

export interface ITextBlock {
  /**
   * Semantic key identifying the block type/channel.
   * Clients route blocks to UI regions based on key prefix.
   */
  readonly key: string;

  /**
   * Block content - array of plain strings and decorations.
   */
  readonly content: ReadonlyArray<TextContent>;

  /**
   * Visual continuation hint. When `true`, the renderer must collapse
   * the paragraph margin between this block and its predecessor so the
   * two lines appear flush (no inter-line gap beyond `line-height`).
   *
   * Used to express multi-line content where every line is its own
   * block (so blocks carry no intra-content `\n`), while preserving
   * the tight visual stacking that single-block-with-newlines used to
   * provide. Invariant: a `tight` block must not appear first in a
   * packet — the renderer relies on a predecessor to collapse against.
   */
  readonly tight?: boolean;

  /**
   * Optional semantic CSS class applied to the rendered element by the
   * browser main-channel renderer (in addition to `main-entry`). Used
   * for content with its own visual identity — game banner pieces
   * (`game-title`, `story-version`, `platform-version`, `sub-title`,
   * `author-list`, `banner-spacer`), and similar future categories.
   * Author classes (no `sharpee-` prefix) flow through unchanged so
   * stories can define their own.
   */
  readonly className?: string;

  /**
   * The player's presence relative to where the narrated event happened
   * (ADR-328 D3). Copied by the prose pipeline from the source event's
   * `presence` tag; absent on blocks whose event carried no tag (the
   * player's own actions, system messages). The union mirrors
   * `@sharpee/core`'s `Presence` verbatim — this package has no
   * dependencies, so it cannot import it.
   *
   * Presentation is the client's: the default renderers hide `absent`
   * and show `present`/`concealed`; an omniscient client shows all.
   */
  readonly presence?: 'present' | 'absent' | 'concealed';

  /**
   * The id of the place the narrated event happened in (ADR-328 D3) —
   * a room or region entity id, from the source event's
   * `entities.location`. Set only alongside `presence`. A client that
   * shows off-stage narration labels it by this location.
   */
  readonly location?: string;

  /**
   * The message the block was rendered from (ADR-333 D1). Stamped by the
   * engine's phrase path on every block it realizes; carried unchanged
   * through the channel wire so a client can address the paragraph's
   * source. See {@link IBlockSource}.
   */
  readonly source?: IBlockSource;
}

/**
 * Core block keys defined by the platform.
 * Stories can emit custom keys.
 */
export const CORE_BLOCK_KEYS = {
  /** Room title */
  ROOM_NAME: 'room.name',
  /** Room description */
  ROOM_DESCRIPTION: 'room.description',
  /** Items visible in room */
  ROOM_CONTENTS: 'room.contents',
  /** Action success result */
  ACTION_RESULT: 'action.result',
  /** Action blocked message */
  ACTION_BLOCKED: 'action.blocked',
  /** Current room (status bar) */
  STATUS_ROOM: 'status.room',
  /** Current score (status bar) */
  STATUS_SCORE: 'status.score',
  /** Turn count (status bar) */
  STATUS_TURNS: 'status.turns',
  /** System error */
  ERROR: 'error',
  /** Command prompt */
  PROMPT: 'prompt',
  /** Game/story message */
  GAME_MESSAGE: 'game.message',
  /** Game opening banner */
  GAME_BANNER: 'game.banner',
} as const;
